import { useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { t } from '@ticarishift/i18n';
import { loginPath } from '../shared/auth/next.ts';
import { homeFor, useSession, useSessionState } from '../shared/auth/session.tsx';
import { ConnectionErrorScreen, ForbiddenPage, LoadingScreen } from './pages/SystemPages.tsx';
import type { RouteDef } from './routes.config.ts';

function Unreachable() {
  const { retry } = useSessionState();
  const [retrying, setRetrying] = useState(false);
  const run = () => {
    setRetrying(true);
    void retry().finally(() => setRetrying(false));
  };

  return <ConnectionErrorScreen onRetry={run} retrying={retrying} />;
}

/**
 * Korunan rotalar (S16-K1): platform oturumu yalnız iki adımlı doğrulamadan sonra açılır; oturum yoksa `/login?next=…`.
 * Oturum ve rol yüklenirken marka ekranı; sunucuya ulaşılamazsa "Bağlantı kurulamadı" (oturum korunur). 8 saatlik mutlak
 * ömür dolunca istek 401 alır, yenileme reddedilir ve oturum açma ekranı "süreniz doldu" açıklamasıyla açılır.
 */
export function RequireSession() {
  const { status } = useSessionState();
  const location = useLocation();

  switch (status.kind) {
    case 'loading':
      return <LoadingScreen label={t('backoffice.system.resolving')} />;
    case 'unreachable':
      return <Unreachable />;
    case 'anonymous':
      // Kendi kapattığı oturumdan sonra dönüş yolu taşınmaz; süresi dolan ya da başka sekmede kapanan oturumda taşınır.
      return <Navigate to={status.notice === 'signedOut' ? '/login' : loginPath(`${location.pathname}${location.search}${location.hash}`)} replace />;
    case 'ready':
      return <Outlet />;
  }
}

/**
 * Rota kapısı: sayfanın platform yetkisi yoksa 403 ekranı gereken yetkinin adını yazar. Genel Bakış'ı göremeyen rol
 * (faturalama) kökte kendi ana sayfasına yönlenir.
 */
export function RouteGate({ route, children }: { route: RouteDef; children: ReactNode }) {
  const { identity } = useSession();
  if (identity.permissions.has(route.permission)) {
    return children;
  }

  const home = homeFor(identity);
  if (route.path === '/' && home !== '/') {
    return <Navigate to={home} replace />;
  }

  return <ForbiddenPage permission={route.permission} home={home} />;
}
